import React from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { auth } from './firebase';

const ALLOWED_DOMAIN = 'rezi.io';

const isAllowedEmail = (email?: string | null) => {
  if (!email) {
    return false;
  }
  return email.toLowerCase().endsWith(`@${ALLOWED_DOMAIN}`);
};

type AuthState = {
  user: User | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  signOutUser: () => Promise<void>;
};

const AuthContext = React.createContext<AuthState | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      if (nextUser && !isAllowedEmail(nextUser.email)) {
        await signOut(auth);
        setUser(null);
        setError(`Use a @${ALLOWED_DOMAIN} Google account.`);
        setLoading(false);
        return;
      }

      setUser(nextUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = React.useCallback(async () => {
    setError(null);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    try {
      const result = await signInWithPopup(auth, provider);
      if (!isAllowedEmail(result.user.email)) {
        await signOut(auth);
        setError(`Use a @${ALLOWED_DOMAIN} Google account.`);
      }
    } catch (err) {
      setError('Unable to sign in with Google.');
    }
  }, []);

  const signOutUser = React.useCallback(async () => {
    await signOut(auth);
  }, []);

  const value = React.useMemo(
    () => ({
      user,
      loading,
      error,
      signInWithGoogle,
      signOutUser,
    }),
    [user, loading, error, signInWithGoogle, signOutUser],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
};

export const useAuth = () => {
  const context = React.use(AuthContext);
  if (!context) {
    throw new Error('AuthProvider is missing');
  }
  return context;
};
